import axios from 'axios';
import { toast } from 'sonner';

import { API_BASE as BASE } from '../lib/apiBase';
const api = axios.create({ baseURL: `${BASE}/api`, timeout: 15000 });

// Attach JWT to every request from localStorage
api.interceptors.request.use((config) => {
  const tok = localStorage.getItem('monteurcentraal_token');
  if (tok) config.headers.Authorization = `Bearer ${tok}`;
  return config;
});

// Global 402 (plan limit) handler — toon altijd een duidelijke upgrade-prompt
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 402) {
      const msg = err.response.data?.detail || 'Limiet bereikt op huidig plan';
      toast.error(msg, {
        description: 'Upgrade je plan om door te gaan',
        action: { label: 'Bekijk plannen', onClick: () => window.open('/pricing', '_blank') },
        duration: 8000,
      });
    }
    return Promise.reject(err);
  }
);

// Aparte client voor het klantportaal (eigen JWT, los van medewerker-token)
const KLANT_TOKEN_KEY = 'mc_klant_token';
const portaalApi = axios.create({ baseURL: `${BASE}/api`, timeout: 20000 });
portaalApi.interceptors.request.use((config) => {
  const tok = localStorage.getItem(KLANT_TOKEN_KEY);
  if (tok) config.headers.Authorization = `Bearer ${tok}`;
  return config;
});

export const portaal = {
  tokenKey: KLANT_TOKEN_KEY,
  isIngelogd: () => !!localStorage.getItem(KLANT_TOKEN_KEY),
  setToken: (t) => localStorage.setItem(KLANT_TOKEN_KEY, t),
  logout: () => localStorage.removeItem(KLANT_TOKEN_KEY),
  register: (garageId, data) => portaalApi.post(`/portaal/${garageId}/register`, data).then(r => r.data),
  login: (garageId, data) => portaalApi.post(`/portaal/${garageId}/login`, data).then(r => r.data),
  me: () => portaalApi.get('/portaal/me').then(r => r.data),
  afspraken: () => portaalApi.get('/portaal/afspraken').then(r => r.data),
  facturen: () => portaalApi.get('/portaal/facturen').then(r => r.data),
  voertuigen: () => portaalApi.get('/portaal/voertuigen').then(r => r.data),
  historie: () => portaalApi.get('/portaal/historie').then(r => r.data),
  afspraakOpties: () => portaalApi.get('/portaal/afspraak-opties').then(r => r.data),
  aanvraagAfspraak: (data) => portaalApi.post('/portaal/afspraken', data).then(r => r.data),
};


export const carsysApi = {
  // Klanten
  getKlanten: () => api.get('/klanten').then(r => r.data),
  createKlant: (data) => api.post('/klanten', data).then(r => r.data),
  updateKlant: (id, data) => api.put(`/klanten/${id}`, data).then(r => r.data),
  deleteKlant: (id) => api.delete(`/klanten/${id}`).then(r => r.data),

  // Klant auto's
  getKlantAutos: () => api.get('/klant-autos').then(r => r.data),
  createKlantAuto: (data) => api.post('/klant-autos', data).then(r => r.data),
  updateKlantAuto: (kenteken, data) => api.put(`/klant-autos/${encodeURIComponent(kenteken)}`, data).then(r => r.data),
  deleteKlantAuto: (kenteken) => api.delete(`/klant-autos/${encodeURIComponent(kenteken)}`).then(r => r.data),
  archiveerKlantAuto: (kenteken, data) => api.post(`/klant-autos/${encodeURIComponent(kenteken)}/archiveer`, data).then(r => r.data),
  herstelKlantAuto: (kenteken) => api.post(`/klant-autos/${encodeURIComponent(kenteken)}/herstel`).then(r => r.data),
  getVerkoopBronnen: () => api.get('/bonnen/verkoop-bronnen').then(r => r.data),
  setPrimaryKlantAuto: (kenteken) => api.post(`/klant-autos/${encodeURIComponent(kenteken)}/set-primary`).then(r => r.data),

  // Bonnen
  getBonnen: () => api.get('/bonnen').then(r => r.data),
  createBon: (data) => api.post('/bonnen', data).then(r => r.data),
  updateBon: (id, data) => api.put(`/bonnen/${id}`, data).then(r => r.data),
  setBonStatus: (id, payload) => api.post(`/bonnen/${id}/status`, payload).then(r => r.data),
  convertBon: (id, target) => api.post(`/bonnen/${id}/convert`, { target }).then(r => r.data),
  creditBon: (id) => api.post(`/bonnen/${id}/credit`).then(r => r.data),
  deelfactuurBon: (id, payload) => api.post(`/bonnen/${id}/deelfactuur`, payload).then(r => r.data),
  createAkkoordLink: (id) => api.post(`/bonnen/${id}/akkoord-link`).then(r => r.data),
  getPublicWerkorder: (token) => api.get(`/public/werkorder/${token}`).then(r => r.data),
  submitWerkorderAkkoord: (token, data) => api.post(`/public/werkorder/${token}/akkoord`, data).then(r => r.data),
  exportFacturenUrl: (year) => `${BASE}/api/boekhouding/facturen/export${year ? `?year=${year}` : ''}`,
  deleteBon: (id) => api.delete(`/bonnen/${id}`).then(r => r.data),

  // Betalingen
  getBetalingen: () => api.get('/betalingen').then(r => r.data),
  createBetaling: (data) => api.post('/betalingen', data).then(r => r.data),
  updateBetaling: (id, data) => api.put(`/betalingen/${id}`, data).then(r => r.data),
  deleteBetaling: (id) => api.delete(`/betalingen/${id}`).then(r => r.data),

  // Kosten / inkoopfacturen
  getKosten: () => api.get('/kosten').then(r => r.data),
  createKost: (data) => api.post('/kosten', data).then(r => r.data),
  updateKost: (id, data) => api.put(`/kosten/${id}`, data).then(r => r.data),
  deleteKost: (id) => api.delete(`/kosten/${id}`).then(r => r.data),

  // Bankafschrift-import (MT940 / CAMT.053)
  parseBankFile: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/bank/import/parse', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 }).then(r => r.data);
  },
  confirmBankImport: (items) => api.post('/bank/import/confirm', { items }).then(r => r.data),

  // Online afspraken — beheer (authed)
  getBookingSettings: () => api.get('/booking/settings').then(r => r.data),
  saveBookingSettings: (data) => api.post('/booking/settings', data).then(r => r.data),
  // Online afspraken — publiek (geen auth nodig)
  getPublicBooking: (garageId) => api.get(`/public/booking/${garageId}`).then(r => r.data),
  getPublicSlots: (garageId, date, dienstId) => api.get(`/public/booking/${garageId}/slots`, { params: { date, dienstId } }).then(r => r.data),
  createPublicBooking: (garageId, data) => api.post(`/public/booking/${garageId}`, data).then(r => r.data),
  publicRdwLookup: (kenteken) => api.get(`/rdw/${encodeURIComponent(kenteken)}`).then(r => r.data),

  // Apparaten — activatielogboek + apparaatlimiet
  deviceHeartbeat: (data) => api.post('/license/devices/heartbeat', data).then(r => r.data),
  getDevices: () => api.get('/license/devices').then(r => r.data),
  deleteDevice: (deviceId) => api.delete(`/license/devices/${deviceId}`).then(r => r.data),

  // Debiteuren — automatische herinneringen
  getReminderSettings: () => api.get('/debiteuren/instellingen').then(r => r.data),
  setReminderSettings: (data) => api.post('/debiteuren/instellingen', data).then(r => r.data),
  runReminders: () => api.post('/debiteuren/herinneringen/run').then(r => r.data),

  // APK — automatische herinneringen
  getApkReminderSettings: () => api.get('/apk-reminders/instellingen').then(r => r.data),
  setApkReminderSettings: (data) => api.post('/apk-reminders/instellingen', data).then(r => r.data),
  runApkReminders: () => api.post('/apk-reminders/run').then(r => r.data),
  refreshApkRdw: () => api.post('/apk/refresh-rdw').then(r => r.data),
  getOfferteConversie: () => api.get('/stats/offerte-conversie').then(r => r.data),
  sendWeeklySummary: () => api.post('/stats/weekly-summary/send').then(r => r.data),
  getWeeklySummarySettings: () => api.get('/stats/weekly-summary/instellingen').then(r => r.data),
  setWeeklySummarySettings: (enabled) => api.post('/stats/weekly-summary/instellingen', { enabled }).then(r => r.data),
  getApkReminderLog: () => api.get('/apk-reminders/log').then(r => r.data),

  // Online factuurbetaling (Stripe, per garage)
  getBetalingInstellingen: () => api.get('/betaling/instellingen').then(r => r.data),
  setBetalingInstellingen: (data) => api.post('/betaling/instellingen', data).then(r => r.data),
  createBetaallink: (bid) => api.post(`/bonnen/${bid}/betaallink`).then(r => r.data),
  getPublicFactuur: (token) => api.get(`/public/factuur/${token}`).then(r => r.data),
  publicFactuurCheckout: (token, originUrl) => api.post(`/public/factuur/${token}/checkout`, { origin_url: originUrl }).then(r => r.data),
  publicFactuurStatus: (sessionId) => api.get(`/public/factuur/betaling/status/${sessionId}`).then(r => r.data),

  // Reviews (Google-recensies)
  getReviewInstellingen: () => api.get('/reviews/instellingen').then(r => r.data),
  setReviewInstellingen: (data) => api.post('/reviews/instellingen', data).then(r => r.data),
  sendReviewInvite: (bid) => api.post(`/bonnen/${bid}/review-uitnodiging`).then(r => r.data),
  getReviewLog: () => api.get('/reviews/log').then(r => r.data),

  // Boekhouding — grootboek / journaalposten
  getGrootboekRekeningen: () => api.get('/grootboek/rekeningen').then(r => r.data),
  getJournaalposten: () => api.get('/journaalposten').then(r => r.data),
  genereerBoekhouding: () => api.post('/boekhouding/genereer').then(r => r.data),
  resetBoekhouding: () => api.post('/boekhouding/reset').then(r => r.data),
  createJournaalpost: (data) => api.post('/journaalposten', data).then(r => r.data),
  deleteJournaalpost: (id) => api.delete(`/journaalposten/${id}`).then(r => r.data),

  // Kwartaalrapport naar boekhouder
  getRapportSettings: () => api.get('/boekhouding/rapport/instellingen').then(r => r.data),
  setRapportSettings: (data) => api.post('/boekhouding/rapport/instellingen', data).then(r => r.data),
  verstuurRapport: (year, quarter) => api.post(`/boekhouding/rapport/verstuur?year=${year}&quarter=${quarter}`).then(r => r.data),

  // Auto handel
  getHandelAutos: () => api.get('/handelautos').then(r => r.data),
  createHandelAuto: (data) => api.post('/handelautos', data).then(r => r.data),
  updateHandelAuto: (id, data) => api.put(`/handelautos/${id}`, data).then(r => r.data),
  deleteHandelAuto: (id) => api.delete(`/handelautos/${id}`).then(r => r.data),

  // Other
  getPlanning: () => api.get('/planning').then(r => r.data),
  createPlanning: (data) => api.post('/planning', data).then(r => r.data),
  updatePlanning: (id, data) => api.put(`/planning/${id}`, data).then(r => r.data),
  deletePlanning: (id) => api.delete(`/planning/${id}`).then(r => r.data),
  notifyPlanning: (id, payload) => api.post(`/planning/${id}/notify`, payload).then(r => r.data),

  getOnderdelen: () => api.get('/onderdelen').then(r => r.data),
  createOnderdeel: (data) => api.post('/onderdelen', data).then(r => r.data),
  updateOnderdeel: (id, data) => api.put(`/onderdelen/${id}`, data).then(r => r.data),
  deleteOnderdeel: (id) => api.delete(`/onderdelen/${id}`).then(r => r.data),
  onderdeelHistorie: (id) => api.get(`/onderdelen/${id}/historie`).then(r => r.data),

  // Epic G — Inkoop & leveranciers
  getLeveranciers: () => api.get('/leveranciers').then(r => r.data),
  createLeverancier: (data) => api.post('/leveranciers', data).then(r => r.data),
  updateLeverancier: (id, data) => api.put(`/leveranciers/${id}`, data).then(r => r.data),
  deleteLeverancier: (id) => api.delete(`/leveranciers/${id}`).then(r => r.data),
  getInkoopOrders: () => api.get('/inkooporders').then(r => r.data),
  createInkoopOrder: (data) => api.post('/inkooporders', data).then(r => r.data),
  updateInkoopOrder: (id, data) => api.put(`/inkooporders/${id}`, data).then(r => r.data),
  deleteInkoopOrder: (id) => api.delete(`/inkooporders/${id}`).then(r => r.data),
  setInkoopOrderStatus: (id, payload) => api.post(`/inkooporders/${id}/status`, payload).then(r => r.data),
  ontvangstInkoopOrder: (id, payload) => api.post(`/inkooporders/${id}/ontvangst`, payload).then(r => r.data),
  getGarantieclaims: () => api.get('/garantieclaims').then(r => r.data),
  createGarantieclaim: (data) => api.post('/garantieclaims', data).then(r => r.data),
  updateGarantieclaim: (id, data) => api.put(`/garantieclaims/${id}`, data).then(r => r.data),
  deleteGarantieclaim: (id) => api.delete(`/garantieclaims/${id}`).then(r => r.data),

  getDocumenten: () => api.get('/documenten').then(r => r.data),
  createDocument: (data) => api.post('/documenten', data).then(r => r.data),
  deleteDocument: (id) => api.delete(`/documenten/${id}`).then(r => r.data),

  getMeldingen: () => api.get('/meldingen').then(r => r.data),
  refreshMeldingen: () => api.post('/meldingen/refresh', {}).then(r => r.data),

  // Audit + mail
  getAudit: () => api.get('/audit').then(r => r.data),
  getMailLog: () => api.get('/maillog').then(r => r.data),
  sendEmail: (data) => api.post('/email/send', data).then(r => r.data),

  // Plan info
  getPlanInfo: () => api.get('/plan/info').then(r => r.data),

  // License re-verify
  verifyLicense: () => api.post('/license/verify').then(r => r.data),

  // Cloud + License
  getCloudStatus: () => api.get('/cloud/status').then(r => r.data),
  startDropboxOAuth: () => api.get('/oauth/dropbox/start').then(r => r.data),
  startGoogleOAuth: () => api.get('/oauth/google/start').then(r => r.data),
  disconnectCloud: () => api.post('/cloud/disconnect').then(r => r.data),
  activateLicense: (key) => api.post('/license/activate', { key }).then(r => r.data),
  deactivateLicense: () => api.post('/license/deactivate').then(r => r.data),
  uploadDocument: (file, meta) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('type', meta.type || 'Document');
    fd.append('klant', meta.klant || '-');
    fd.append('kenteken', meta.kenteken || '');
    return api.post('/documenten/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  downloadDocumentUrl: (id) => `${BASE}/api/documenten/${id}/download`,

  // AI Diagnose — STAP 1: analyseer PDF (schrijft NIETS weg, geeft voorstel + confidence terug)
  analyseDiagnose: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/diagnose/analyse', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000 }).then(r => r.data);
  },
  // AI Diagnose — STAP 2: leg de (gecontroleerde) diagnose definitief vast
  confirmDiagnose: (payload) => api.post('/diagnose/confirm', payload, { timeout: 60000 }).then(r => r.data),

  // AI verkoopadvertentie voor een handelauto (Marktplaats / AutoScout24 / Facebook) + indicatieve marktprijs
  generateAdvertentie: (auto) => api.post('/handelautos/advertentie', auto, { timeout: 60000 }).then(r => r.data),

  // AI verkoopadvertentie voor een los onderdeel (Marktplaats / Facebook / Forum) + indicatieve marktprijs + fotoanalyse
  generatePartAdvertentie: (part) => api.post('/parts/advertentie', part, { timeout: 60000 }).then(r => r.data),
  partFotoAnalyse: (payload) => api.post('/parts/foto-analyse', payload, { timeout: 60000 }).then(r => r.data),
  partCompatibiliteit: (part) => api.post('/parts/compatibiliteit', part, { timeout: 60000 }).then(r => r.data),
  partWaardebepaling: (part) => api.post('/parts/waardebepaling', part, { timeout: 60000 }).then(r => r.data),
  aiFoutcode: (payload) => api.post('/ai/foutcode', payload, { timeout: 60000 }).then(r => r.data),
  aiOfferte: (payload) => api.post('/ai/offerte', payload, { timeout: 60000 }).then(r => r.data),
  partsCatalogus: (data) => api.post('/parts/catalogus', data, { timeout: 60000 }).then(r => r.data),

  // AI: lees autopapieren (foto/scan, ook buitenlands) → gestructureerde voertuiggegevens
  scanVoertuigDocument: (image) => api.post('/voertuig/scan-document', { image }, { timeout: 60000 }).then(r => r.data),
  // Auditfile Financieel (XAF) export voor de accountant
  downloadAuditfile: (jaar) => api.get(`/boekhouding/auditfile?jaar=${jaar}`, { responseType: 'blob' }).then(r => r.data),
  downloadBtwAangifte: (year, quarter) => api.get(`/boekhouding/btw-aangifte?year=${year}&quarter=${quarter}`, { responseType: 'blob' }).then(r => r.data),
  // Herhaalfacturen (periodiek automatisch factureren)
  getHerhaalfacturen: () => api.get('/herhaalfacturen').then(r => r.data),
  createHerhaalfactuur: (data) => api.post('/herhaalfacturen', data).then(r => r.data),
  updateHerhaalfactuur: (id, data) => api.put(`/herhaalfacturen/${id}`, data).then(r => r.data),
  deleteHerhaalfactuur: (id) => api.delete(`/herhaalfacturen/${id}`).then(r => r.data),
  genereerHerhaalfactuurNu: (id) => api.post(`/herhaalfacturen/${id}/genereer-nu`).then(r => r.data),

  // Vaste activa & afschrijvingen
  getActiva: () => api.get('/activa').then(r => r.data),
  getActivaSamenvatting: () => api.get('/activa/samenvatting').then(r => r.data),
  createActiva: (data) => api.post('/activa', data).then(r => r.data),
  updateActiva: (id, data) => api.put(`/activa/${id}`, data).then(r => r.data),
  deleteActiva: (id) => api.delete(`/activa/${id}`).then(r => r.data),
  // AI: klantvriendelijke werkordersamenvatting
  werkorderAiSamenvatting: (bid) => api.post(`/werkorder/${bid}/ai-samenvatting`, {}, { timeout: 60000 }).then(r => r.data),
  // AI: lees een bon/kassabon/inkoopfactuur → gestructureerde kostengegevens
  scanBon: (image) => api.post('/kosten/scan-bon', { image }, { timeout: 60000 }).then(r => r.data),
  // Bon-bijlage (digitaal bonnenarchief)
  uploadKostBijlage: (kid, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/kosten/${kid}/bijlage`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }).then(r => r.data); },
  uploadKostBijlageBase64: (kid, image, filename) => api.post(`/kosten/${kid}/bijlage-base64`, { image, filename }, { timeout: 60000 }).then(r => r.data),
  getKostBijlage: (kid) => api.get(`/kosten/${kid}/bijlage`, { responseType: 'blob' }).then(r => r.data),
  deleteKostBijlage: (kid) => api.delete(`/kosten/${kid}/bijlage`).then(r => r.data),


  // Onderdelenhandel — gebruikte & nieuwe onderdelen + sloopauto's (PRO+)
  getParts: (params = {}) => api.get('/parts', { params }).then(r => r.data),
  createPart: (data) => api.post('/parts', data).then(r => r.data),
  updatePart: (id, data) => api.put(`/parts/${id}`, data).then(r => r.data),
  deletePart: (id) => api.delete(`/parts/${id}`).then(r => r.data),
  verkoopPart: (id, data) => api.post(`/parts/${id}/verkoop`, data).then(r => r.data),
  verkoopPartFactuur: (id, data) => api.post(`/parts/${id}/verkoop-factuur`, data).then(r => r.data),
  verkoopPart: (id, data = {}) => api.post(`/parts/${id}/verkoop`, data).then(r => r.data),
  getPartsStats: () => api.get('/parts/stats').then(r => r.data),
  getSloopautos: () => api.get('/sloopautos').then(r => r.data),
  createSloopauto: (data) => api.post('/sloopautos', data).then(r => r.data),
  updateSloopauto: (id, data) => api.put(`/sloopautos/${id}`, data).then(r => r.data),
  deleteSloopauto: (id) => api.delete(`/sloopautos/${id}`).then(r => r.data),
  genereerOnderdelen: (id, payload) => api.post(`/sloopautos/${id}/genereer-onderdelen`, payload || {}).then(r => r.data),

  // Publieke showroom (beheer; publieke pagina gebruikt losse axios-calls zonder token)
  publishShowroom: (data) => api.post('/showroom/publish', data).then(r => r.data),
  getShowroomMine: () => api.get('/showroom/mine').then(r => r.data),
  getShowroomStatusForBron: (bronId) => api.get(`/showroom/status/${encodeURIComponent(bronId)}`).then(r => r.data),
  setShowroomStatus: (id, status) => api.put(`/showroom/${id}/status`, { status }).then(r => r.data),
  deleteShowroom: (id) => api.delete(`/showroom/${id}`).then(r => r.data),
  getShowroomStats: () => api.get('/showroom/stats').then(r => r.data),
  // Publieke showroom (geen auth nodig)
  getPublicShowroom: (slug) => api.get(`/public/showroom/${encodeURIComponent(slug)}`).then(r => r.data),
  getPublicShowroomListing: (slug, id) => api.get(`/public/showroom/${encodeURIComponent(slug)}/listing/${id}`).then(r => r.data),
  publicShowroomContact: (slug, id, kanaal) => api.post(`/public/showroom/${encodeURIComponent(slug)}/listing/${id}/contact`, { kanaal }).then(r => r.data),
  showroomLogoUrl: (slug) => `${BASE}/api/public/showroom/${encodeURIComponent(slug)}/logo`,
  showroomBannerUrl: (slug) => `${BASE}/api/public/showroom/${encodeURIComponent(slug)}/banner`,
  uploadShowroomBanner: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/gegevens/showroom-banner', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data); },
  deleteShowroomBanner: () => api.delete('/gegevens/showroom-banner').then(r => r.data),

  // Backup
  backupUrl: () => `${BASE}/api/backup/export`,
  backupImport: (snapshot) => api.post('/backup/import', snapshot).then(r => r.data),
  backupToCloud: () => api.post('/backup/cloud').then(r => r.data),
  setAutoBackup: (enabled) => api.post('/backup/cloud/auto', { enabled }).then(r => r.data),

  // Gegevens
  getGegevens: () => api.get('/gegevens').then(r => r.data),
  updateGegevens: (data) => api.put('/gegevens', data).then(r => r.data),

  // Dashboard
  getDashboardStats: () => api.get('/dashboard/stats').then(r => r.data),

  // External integrations
  rdwLookup: (kenteken) => api.get(`/rdw/${encodeURIComponent(kenteken)}`).then(r => r.data),
  getRecalls: () => api.get('/recalls').then(r => r.data),
  createRecall: (data) => api.post('/recalls', data).then(r => r.data),
  deleteRecall: (id) => api.delete(`/recalls/${id}`).then(r => r.data),
  getBandensets: () => api.get('/bandensets').then(r => r.data),
  createBandenset: (data) => api.post('/bandensets', data).then(r => r.data),
  updateBandenset: (id, data) => api.put(`/bandensets/${id}`, data).then(r => r.data),
  deleteBandenset: (id) => api.delete(`/bandensets/${id}`).then(r => r.data),
  vinLookup: (vin) => api.get(`/vin/${encodeURIComponent(vin)}`).then(r => r.data),
  getSuggesties: () => api.get('/suggesties').then(r => r.data),
  updateSuggesties: (regels) => api.put('/suggesties', { regels }).then(r => r.data),
  resetSuggesties: () => api.post('/suggesties/reset').then(r => r.data),
  adresLookup: (postcode, huisnummer) => api.get('/adres', { params: { postcode, huisnummer } }).then(r => r.data),
  adresLookupByStraat: (straat, huisnummer, plaats) => api.get('/adres', { params: { straat, huisnummer, plaats } }).then(r => r.data),
  adresSuggest: (q, kind = 'woonplaats') => api.get('/adres/suggest', { params: { q, kind } }).then(r => r.data.results),

  // Logo (PRO+)
  uploadLogo: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/gegevens/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  deleteLogo: () => api.delete('/gegevens/logo').then(r => r.data),
  logoUrl: (garageId) => `${BASE}/api/gegevens/logo/${garageId}`,

  // Voorschotten + verrekenen
  getVoorschotten: () => api.get('/voorschotten').then(r => r.data),
  createVoorschot: (data) => api.post('/voorschotten', data).then(r => r.data),
  updateVoorschot: (id, data) => api.put(`/voorschotten/${id}`, data).then(r => r.data),
  markVoorschotVerrekend: (id) => api.post(`/voorschotten/${id}/verrekenen`).then(r => r.data),
  deleteVoorschot: (id) => api.delete(`/voorschotten/${id}`).then(r => r.data),
  getVerrekenenSummary: () => api.get('/verrekenen/summary').then(r => r.data),
  setTeamShare: (userId, winstAandeel) => api.put(`/auth/team/${userId}/share`, { winstAandeel }).then(r => r.data),
  getTeam: () => api.get('/auth/team').then(r => r.data),
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { currentPassword, newPassword }).then(r => r.data),

  // E-facturatie (UBL / Peppol BIS 3.0)
  ublFactuurUrl: (bid) => `${BASE}/api/factuur/${bid}/ubl`,
  downloadUbl: (bid) => api.get(`/factuur/${bid}/ubl`, { responseType: 'blob' }).then(r => r.data),

  // SEPA-incasso
  getSepaInstellingen: () => api.get('/sepa/instellingen').then(r => r.data),
  setSepaInstellingen: (data) => api.post('/sepa/instellingen', data).then(r => r.data),
  getSepaMachtigingen: () => api.get('/sepa/machtigingen').then(r => r.data),
  createSepaMachtiging: (data) => api.post('/sepa/machtigingen', data).then(r => r.data),
  updateSepaMachtiging: (id, data) => api.put(`/sepa/machtigingen/${id}`, data).then(r => r.data),
  deleteSepaMachtiging: (id) => api.delete(`/sepa/machtigingen/${id}`).then(r => r.data),
  getSepaIncasseerbaar: () => api.get('/sepa/incasseerbaar').then(r => r.data),
  genereerSepaBatch: (data) => api.post('/sepa/incassobatch', data, { responseType: 'blob' }).then(r => r.data),

  // Peppol (Storecove)
  getPeppolInstellingen: () => api.get('/efactuur/peppol/instellingen').then(r => r.data),
  setPeppolInstellingen: (data) => api.post('/efactuur/peppol/instellingen', data).then(r => r.data),
  sendPeppol: (bid, data) => api.post(`/factuur/${bid}/peppol/send`, data).then(r => r.data),

  // SMS/WhatsApp afspraakherinneringen (Twilio)
  getMessagingInstellingen: () => api.get('/messaging/instellingen').then(r => r.data),
  setMessagingInstellingen: (data) => api.post('/messaging/instellingen', data).then(r => r.data),
  testMessaging: (telefoon) => api.post('/messaging/test', { telefoon }).then(r => r.data),
  runMessaging: () => api.post('/messaging/run').then(r => r.data),
  sendPlanningReminder: (pid) => api.post(`/planning/${pid}/reminder`).then(r => r.data),

  // Onboarding / setup-gids
  getOnboardingStatus: () => api.get('/onboarding/status').then(r => r.data),
  dismissOnboarding: () => api.post('/onboarding/dismiss').then(r => r.data),
  reopenOnboarding: () => api.post('/onboarding/reopen').then(r => r.data),
  markOnboardingCelebrated: () => api.post('/onboarding/celebrate-seen').then(r => r.data),
  skipIntegration: (key, skip = true) => api.post('/onboarding/skip', { key, skip }).then(r => r.data),
  getIntegrationsOverview: () => api.get('/integrations/overview').then(r => r.data),
};

export default carsysApi;
